import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';

interface OptimizerProps {
  isAdmin: boolean;
}

interface OptRun {
  id: string;
  status: string;
  trigger: string;
  loop_type: string;
  proposals_generated: number;
  created_at: string;
  completed_at: string;
  ai_model: string;
  token_usage: number;
  cost_usd: number;
}

interface Proposal {
  id: string;
  run_id: string;
  status: string;
  target: string;
  proposed_changes: Record<string, any>;
  change_rationale: string;
  baseline_metrics: Record<string, any>;
  proposed_metrics: Record<string, any>;
  improvement_summary: string;
  current_config: Record<string, any>;
  created_at: string;
  approved_at: string;
}

const STATUS_COLORS: Record<string, string> = {
  pending: '#f59e0b',
  approved: '#10b981',
  rejected: '#ef4444',
  deployed: '#3b82f6',
  expired: '#6b7280',
  running: '#3b82f6',
  completed: '#10b981',
  failed: '#ef4444',
  no_action: '#6b7280',
  queued: '#a78bfa',
};

const TARGET_LABELS: Record<string, string> = {
  donchian_config: 'Donchian Bot',
  signal_config_smc: 'SMC Signals',
  signal_config_priceaction: 'Price Action Signals',
  signal_config_technical: 'Technical Signals',
  signal_config_resolution: 'Resolution Config',
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span style={{
    background: STATUS_COLORS[status] || '#6b7280',
    color: '#fff',
    padding: '2px 10px',
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
  }}>
    {status}
  </span>
);

const Optimizer: React.FC<OptimizerProps> = ({ isAdmin }) => {
  const [runs, setRuns] = useState<OptRun[]>([]);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [activeReport, setActiveReport] = useState<string | null>(null);
  const [reportHtml, setReportHtml] = useState('');
  const [triggering, setTriggering] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    try {
      const [runsData, proposalsData] = await Promise.all([
        api.getOptimizerRuns(),
        api.getOptimizerProposals(),
      ]);
      setRuns(runsData);
      setProposals(proposalsData);
    } catch (e: any) {
      setError(e.message);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    loadData();
  }, [isAdmin, loadData]);

  const handleTrigger = async () => {
    setTriggering(true);
    setError('');
    try {
      await api.triggerOptimizer();
      await loadData();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setTriggering(false);
    }
  };

  const handleAction = async (proposalId: string, action: string) => {
    let reason: string | undefined;
    if (action === 'reject') {
      const input = window.prompt('Rejection reason (helps the optimizer avoid re-proposing):');
      if (input === null) return; // cancelled
      reason = input || undefined;
    }
    setActionLoading(proposalId);
    try {
      await api.actionOptimizerProposal(proposalId, action, reason);
      await loadData();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setActionLoading(null);
    }
  };

  const viewReport = async (runId: string) => {
    try {
      const html = await api.getOptimizerReport(runId);
      setReportHtml(html);
      setActiveReport(runId);
    } catch (e: any) {
      setError(e.message);
    }
  };

  if (!isAdmin) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ background: '#1e1e2e', borderRadius: 12, padding: 32, textAlign: 'center' }}>
          <h2 style={{ color: '#f1f5f9', marginBottom: 8 }}>Admin Access Required</h2>
          <p style={{ color: '#71717a' }}>The optimizer is only available to admin users.</p>
        </div>
      </div>
    );
  }

  const pendingProposals = proposals.filter(p => p.status === 'pending');
  const pastProposals = proposals.filter(p => p.status !== 'pending');
  const lastRun = runs[0];

  return (
    <div style={{ padding: 24, maxWidth: 1100 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, color: '#f1f5f9' }}>AI Optimizer</h1>
          <p style={{ margin: '4px 0 0', color: '#71717a', fontSize: 13 }}>
            Dual-loop optimization: Donchian bot config + Signal methodology parameters
          </p>
        </div>
        <button
          onClick={handleTrigger}
          disabled={triggering}
          style={{
            background: triggering ? '#374151' : '#3b82f6',
            color: '#fff',
            border: 'none',
            padding: '10px 20px',
            borderRadius: 8,
            cursor: triggering ? 'not-allowed' : 'pointer',
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          {triggering ? 'Triggering...' : 'Run Now'}
        </button>
      </div>

      {error && (
        <div style={{ background: '#7f1d1d', color: '#fca5a5', padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Status Banner */}
      {lastRun && (
        <div style={{
          background: '#1e1e2e',
          border: '1px solid #2a2d35',
          borderRadius: 12,
          padding: 16,
          marginBottom: 24,
          display: 'flex',
          gap: 24,
          flexWrap: 'wrap',
        }}>
          <div>
            <div style={{ color: '#71717a', fontSize: 11, marginBottom: 2 }}>LAST RUN</div>
            <div style={{ color: '#e4e4e7', fontSize: 14 }}>
              {new Date(lastRun.created_at).toLocaleDateString()} {new Date(lastRun.created_at).toLocaleTimeString()}
            </div>
          </div>
          <div>
            <div style={{ color: '#71717a', fontSize: 11, marginBottom: 2 }}>STATUS</div>
            <StatusBadge status={lastRun.status} />
          </div>
          <div>
            <div style={{ color: '#71717a', fontSize: 11, marginBottom: 2 }}>PROPOSALS</div>
            <div style={{ color: '#e4e4e7', fontSize: 14 }}>{lastRun.proposals_generated}</div>
          </div>
          <div>
            <div style={{ color: '#71717a', fontSize: 11, marginBottom: 2 }}>TOKENS</div>
            <div style={{ color: '#e4e4e7', fontSize: 14 }}>{(lastRun.token_usage || 0).toLocaleString()}</div>
          </div>
          <div>
            <div style={{ color: '#71717a', fontSize: 11, marginBottom: 2 }}>COST</div>
            <div style={{ color: '#e4e4e7', fontSize: 14 }}>${lastRun.cost_usd || '0.00'}</div>
          </div>
          <div>
            <div style={{ color: '#71717a', fontSize: 11, marginBottom: 2 }}>PENDING</div>
            <div style={{ color: pendingProposals.length > 0 ? '#f59e0b' : '#71717a', fontSize: 14, fontWeight: 600 }}>
              {pendingProposals.length}
            </div>
          </div>
        </div>
      )}

      {/* Pending Proposals */}
      {pendingProposals.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: 18, color: '#f1f5f9', marginBottom: 12 }}>
            Pending Proposals ({pendingProposals.length})
          </h2>
          {pendingProposals.map(p => (
            <div key={p.id} style={{
              background: '#1e1e2e',
              border: '1px solid #2a2d35',
              borderRadius: 12,
              padding: 20,
              marginBottom: 12,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <h3 style={{ margin: 0, color: '#f1f5f9', fontSize: 16 }}>
                    {TARGET_LABELS[p.target] || p.target}
                  </h3>
                  <StatusBadge status={p.status} />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={() => handleAction(p.id, 'approve')}
                    disabled={actionLoading === p.id}
                    style={{
                      background: '#10b981', color: '#fff', border: 'none',
                      padding: '6px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                    }}
                  >
                    {actionLoading === p.id ? '...' : 'Approve'}
                  </button>
                  <button
                    onClick={() => handleAction(p.id, 'reject')}
                    disabled={actionLoading === p.id}
                    style={{
                      background: '#374151', color: '#e4e4e7', border: '1px solid #4b5563',
                      padding: '6px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13,
                    }}
                  >
                    Reject
                  </button>
                </div>
              </div>

              <p style={{ color: '#94a3b8', margin: '0 0 12px', fontSize: 13 }}>{p.change_rationale}</p>

              {p.improvement_summary && (
                <p style={{ color: '#a78bfa', margin: '0 0 12px', fontSize: 13 }}>
                  Expected: {p.improvement_summary}
                </p>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <div>
                  <div style={{ color: '#71717a', fontSize: 11, marginBottom: 4 }}>PROPOSED CHANGES</div>
                  <pre style={{
                    background: '#0f1117', padding: 10, borderRadius: 6,
                    color: '#a5f3fc', fontSize: 12, overflow: 'auto', maxHeight: 200,
                    margin: 0,
                  }}>
                    {JSON.stringify(p.proposed_changes, null, 2)}
                  </pre>
                </div>
                <div>
                  <div style={{ color: '#71717a', fontSize: 11, marginBottom: 4 }}>BASELINE METRICS</div>
                  <pre style={{
                    background: '#0f1117', padding: 10, borderRadius: 6,
                    color: '#e4e4e7', fontSize: 12, overflow: 'auto', maxHeight: 200,
                    margin: 0,
                  }}>
                    {JSON.stringify(p.baseline_metrics, null, 2)}
                  </pre>
                </div>
                <div>
                  <div style={{ color: '#71717a', fontSize: 11, marginBottom: 4 }}>PROPOSED METRICS</div>
                  <pre style={{
                    background: '#0f1117', padding: 10, borderRadius: 6,
                    color: '#e4e4e7', fontSize: 12, overflow: 'auto', maxHeight: 200,
                    margin: 0,
                  }}>
                    {JSON.stringify(p.proposed_metrics, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Run History */}
      <div style={{ marginBottom: 32 }}>
        <h2 style={{ fontSize: 18, color: '#f1f5f9', marginBottom: 12 }}>Run History</h2>
        <div style={{ background: '#1e1e2e', border: '1px solid #2a2d35', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #2a2d35' }}>
                <th style={{ padding: '10px 16px', textAlign: 'left', color: '#71717a', fontWeight: 500 }}>Run ID</th>
                <th style={{ padding: '10px 16px', textAlign: 'left', color: '#71717a', fontWeight: 500 }}>Date</th>
                <th style={{ padding: '10px 16px', textAlign: 'left', color: '#71717a', fontWeight: 500 }}>Status</th>
                <th style={{ padding: '10px 16px', textAlign: 'left', color: '#71717a', fontWeight: 500 }}>Trigger</th>
                <th style={{ padding: '10px 16px', textAlign: 'right', color: '#71717a', fontWeight: 500 }}>Proposals</th>
                <th style={{ padding: '10px 16px', textAlign: 'right', color: '#71717a', fontWeight: 500 }}>Cost</th>
                <th style={{ padding: '10px 16px', textAlign: 'center', color: '#71717a', fontWeight: 500 }}>Report</th>
              </tr>
            </thead>
            <tbody>
              {runs.map(r => (
                <tr key={r.id} style={{ borderBottom: '1px solid #1a1a2e' }}>
                  <td style={{ padding: '10px 16px', color: '#e4e4e7', fontFamily: 'monospace', fontSize: 12 }}>
                    {r.id}
                  </td>
                  <td style={{ padding: '10px 16px', color: '#94a3b8' }}>
                    {new Date(r.created_at).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '10px 16px' }}><StatusBadge status={r.status} /></td>
                  <td style={{ padding: '10px 16px', color: '#94a3b8' }}>{r.trigger}</td>
                  <td style={{ padding: '10px 16px', textAlign: 'right', color: '#e4e4e7' }}>{r.proposals_generated}</td>
                  <td style={{ padding: '10px 16px', textAlign: 'right', color: '#94a3b8' }}>${r.cost_usd || '0.00'}</td>
                  <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                    {r.status === 'completed' || r.status === 'no_action' ? (
                      <button
                        onClick={() => viewReport(r.id)}
                        style={{
                          background: 'none', border: '1px solid #374151', color: '#3b82f6',
                          padding: '4px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 12,
                        }}
                      >
                        View
                      </button>
                    ) : '—'}
                  </td>
                </tr>
              ))}
              {runs.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#71717a' }}>
                    No optimization runs yet. Click "Run Now" to start.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Past Proposals */}
      {pastProposals.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: 18, color: '#f1f5f9', marginBottom: 12 }}>Past Proposals</h2>
          <div style={{ background: '#1e1e2e', border: '1px solid #2a2d35', borderRadius: 12, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #2a2d35' }}>
                  <th style={{ padding: '10px 16px', textAlign: 'left', color: '#71717a', fontWeight: 500 }}>Target</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', color: '#71717a', fontWeight: 500 }}>Status</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', color: '#71717a', fontWeight: 500 }}>Rationale</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', color: '#71717a', fontWeight: 500 }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {pastProposals.map(p => (
                  <tr key={p.id} style={{ borderBottom: '1px solid #1a1a2e' }}>
                    <td style={{ padding: '10px 16px', color: '#e4e4e7' }}>
                      {TARGET_LABELS[p.target] || p.target}
                    </td>
                    <td style={{ padding: '10px 16px' }}><StatusBadge status={p.status} /></td>
                    <td style={{ padding: '10px 16px', color: '#94a3b8', maxWidth: 400 }}>
                      {(p.change_rationale || '').slice(0, 120)}{p.change_rationale?.length > 120 ? '...' : ''}
                    </td>
                    <td style={{ padding: '10px 16px', color: '#71717a' }}>
                      {new Date(p.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Report Modal */}
      {activeReport && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} onClick={() => setActiveReport(null)}>
          <div style={{
            background: '#0f172a', borderRadius: 12, width: '90%', maxWidth: 950,
            maxHeight: '85vh', overflow: 'auto', position: 'relative',
          }} onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setActiveReport(null)}
              style={{
                position: 'absolute', top: 12, right: 16, background: 'none',
                border: 'none', color: '#94a3b8', fontSize: 20, cursor: 'pointer',
              }}
            >
              &times;
            </button>
            <div dangerouslySetInnerHTML={{ __html: reportHtml }} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Optimizer;
